/**
 * The dropship, voiced — the rules are `dropshipMix.ts`. One looping voice a ship (few in a match), easing to each
 * frame's heard values and fading out when it falls silent. 67.72 (DECISIONS 215): four big turbofans with afterburners
 * (before, 69.3b's jets: sound_sheets/dropshipJets6768.ts). Synthesized (no files):
 *   - **the fans**: noise through a band at the fans' roar (wide), and a hiss band over it; the blades' passing, a
 *     sawtooth through a low-pass;
 *   - **the cores**: four tones a hair apart (two sines, two triangles) through a band at their pitch — the whine;
 *   - **the afterburner**: noise through a steep low-pass (the rumble) and a band four times higher (the ripping), their
 *     loudness rippled by two slow sines and a random wobble; the crackle, high-passed noise opened in pops by a
 *     thresholded slow noise (a wave-shaper); lighting, a one-shot whump of low noise;
 *   - **the wash**: noise through a low band, its loudness churned by a slow oscillator (the down-blast on the ground);
 *   - all through the air's low-pass, to its side, into the SFX bus (or a sheet's destination).
 * Cost: one voice, 6 oscillators, 6 noise sources and a wave-shaper, about 50 nodes; a frame, 20 parameter updates.
 * Presentation only.
 */
import { getAudioContext, getSfxBus, initAudio } from './sfx';
import type { DropshipHeard } from './dropshipMix';

export interface DropshipOut { ctx: BaseAudioContext; dest: AudioNode }

interface Voice {
  out: GainNode; air: BiquadFilterNode; side: StereoPannerNode;
  fanBand: BiquadFilterNode; fanGain: GainNode; hissGain: GainNode; blade: OscillatorNode; bladeLow: BiquadFilterNode; bladeGain: GainNode;
  cores: OscillatorNode[]; coreBand: BiquadFilterNode; coreGain: GainNode;
  rumbleLow: BiquadFilterNode; rumbleLow2: BiquadFilterNode; ripBand: BiquadFilterNode; burnerGain: GainNode; rippleA: OscillatorNode; rippleB: OscillatorNode; wobble: BiquadFilterNode;
  crackleGain: GainNode;
  washGain: GainNode;
  /** the burner last heard lit (for its light-off) */
  lit: number;
  sources: AudioScheduledSourceNode[];
}
const noiseBufs = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseBuf(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseBufs.get(ctx);
  if (!b) {
    const len = ctx.sampleRate * 2; b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    noiseBufs.set(ctx, b);
  }
  return b;
}
/** The crackle's gate: nothing under `edge`, then opening fast — a slow noise's peaks become pops. */
const gates = new WeakMap<BaseAudioContext, Float32Array<ArrayBuffer>>();
function gateCurve(ctx: BaseAudioContext): Float32Array<ArrayBuffer> {
  let c = gates.get(ctx);
  if (!c) {
    const n = 1024, edge = 0.55; c = new Float32Array(new ArrayBuffer(n * 4));
    for (let i = 0; i < n; i++) { const x = Math.abs((i / (n - 1)) * 2 - 1); c[i] = x <= edge ? 0 : Math.min(1, ((x - edge) / (1 - edge)) ** 1.5 * 1.6); }
    gates.set(ctx, c);
  }
  return c;
}

/** The burner's lit share past which it has lit (a whump), and under which it has gone out (hysteresis). */
const LIGHT = { on: 0.12, off: 0.04 };

export class DropshipVoice {
  private v: Voice | null = null;
  /** × its loudness (a host's volume). */
  volume = 1;
  /** `oneShot`: told the nodes a one-shot (the light-off) made, for a host's count. */
  constructor(private readonly out?: DropshipOut, private readonly oneShot?: (nodes: number) => void) {}

  get live(): boolean { return this.v !== null; }

  /** This frame's heard values (null: silent — the voice fades out and is let go). */
  update(h: DropshipHeard | null): void {
    const where = this.where();
    if (!where) return;
    const t = where.ctx.currentTime;
    if (!h) { if (this.v) { this.release(this.v, t); this.v = null; } return; }
    const fresh = !this.v;
    this.v ??= this.voice(where.ctx, where.dest);
    const v = this.v, k = 0.1;
    v.fanBand.frequency.setTargetAtTime(h.fan.hz, t, 0.2);
    v.fanGain.gain.setTargetAtTime(h.fan.gain * (1 - h.fan.hiss), t, 0.2);
    v.hissGain.gain.setTargetAtTime(h.fan.gain * h.fan.hiss, t, 0.2);
    v.blade.frequency.setTargetAtTime(h.fan.blade.hz, t, k);
    v.bladeLow.frequency.setTargetAtTime(h.fan.blade.hz * 3, t, k);
    v.bladeGain.gain.setTargetAtTime(h.fan.blade.gain, t, 0.15);
    const spread = [1, 1 + h.core.spread, 1 - h.core.spread * 0.8, 1 + h.core.spread * 2.1];
    v.cores.forEach((o, i) => o.frequency.setTargetAtTime(h.core.pitch * spread[i], t, k));
    v.coreBand.frequency.setTargetAtTime(h.core.pitch, t, k);
    v.coreGain.gain.setTargetAtTime(h.core.gain, t, 0.15);
    v.rumbleLow.frequency.setTargetAtTime(h.burner.hz * 2, t, 0.2);
    v.rumbleLow2.frequency.setTargetAtTime(h.burner.hz * 2, t, 0.2);
    v.ripBand.frequency.setTargetAtTime(h.burner.hz * 4, t, 0.2);
    v.rippleA.frequency.setTargetAtTime(h.burner.ripple, t, 0.3);
    v.rippleB.frequency.setTargetAtTime(h.burner.ripple * 1.63, t, 0.3);
    v.wobble.frequency.setTargetAtTime(h.burner.ripple * 1.5, t, 0.3);
    v.burnerGain.gain.setTargetAtTime(h.burner.gain, t, 0.25);
    v.crackleGain.gain.setTargetAtTime(h.burner.crackle, t, 0.25);
    v.washGain.gain.setTargetAtTime(h.wash.gain, t, 0.3);
    v.air.frequency.setTargetAtTime(h.cutoff, t, 0.2);
    v.side.pan.setTargetAtTime(h.pan, t, 0.1);
    v.out.gain.setTargetAtTime(this.volume, t, 0.2);
    // lighting: a whump (not when first heard already lit — it was lit before it was in earshot)
    if (!fresh && v.lit < LIGHT.on && h.burner.lit >= LIGHT.on) this.whump(where.ctx, v, h, t);
    if (fresh || h.burner.lit >= LIGHT.on || h.burner.lit <= LIGHT.off) v.lit = h.burner.lit;
  }

  stop(): void { const w = this.where(); if (this.v) this.release(this.v, w?.ctx.currentTime ?? 0); this.v = null; }

  private where(): DropshipOut | null {
    if (this.out) return this.out;
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    return ctx && bus && ctx.state !== 'closed' ? { ctx, dest: bus } : null;
  }

  private release(v: Voice, t: number): void {
    v.out.gain.setTargetAtTime(0, t, 0.25);
    for (const s of v.sources) { try { s.stop(t + 1.5); } catch { /* already stopped */ } }
  }

  /** The burner lighting: a short, deep thump of noise, at the rumble's loudness. */
  private whump(ctx: BaseAudioContext, v: Voice, h: DropshipHeard, t: number): void {
    const src = ctx.createBufferSource(); src.buffer = noiseBuf(ctx);
    const low = ctx.createBiquadFilter(); low.type = 'lowpass'; low.frequency.setValueAtTime(320, t); low.frequency.exponentialRampToValueAtTime(70, t + 0.5);
    const g = ctx.createGain(), peak = (h.burner.gain / Math.max(1e-6, h.burner.lit)) * 0.6;   // (the full burner's loudness here)
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(peak, t + 0.03); g.gain.exponentialRampToValueAtTime(0.0005, t + 0.6);
    src.connect(low); low.connect(g); g.connect(v.air);
    src.start(t, Math.random(), 0.7);
    src.onended = () => { src.disconnect(); low.disconnect(); g.disconnect(); };
    this.oneShot?.(3);
  }

  private voice(ctx: BaseAudioContext, dest: AudioNode): Voice {
    const sources: AudioScheduledSourceNode[] = [];
    const gain = (v: number) => { const g = ctx.createGain(); g.gain.value = v; return g; };
    const filter = (type: BiquadFilterType, hz: number, q: number) => { const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = hz; f.Q.value = q; return f; };
    const osc = (type: OscillatorType, hz: number) => { const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz; sources.push(o); return o; };
    const noise = () => { const s = ctx.createBufferSource(); s.buffer = noiseBuf(ctx); s.loop = true; s.loopStart = Math.random(); sources.push(s); return s; };
    const out = gain(0), air = filter('lowpass', 9000, 0.5), side = ctx.createStereoPanner();
    air.connect(side); side.connect(out); out.connect(dest);
    // the fans: the roar of their air (a wide band) and the hiss over it; the blades' passing
    const fanNoise = noise(), fanBand = filter('bandpass', 600, 0.45), fanGain = gain(0), hissBand = filter('bandpass', 4200, 0.8), hissGain = gain(0);
    fanNoise.connect(fanBand); fanBand.connect(fanGain); fanGain.connect(air);
    fanNoise.connect(hissBand); hissBand.connect(hissGain); hissGain.connect(air);
    const blade = osc('sawtooth', 200), bladeLow = filter('lowpass', 600, 0.7), bladeGain = gain(0);
    blade.connect(bladeLow); bladeLow.connect(bladeGain); bladeGain.connect(air);
    // the cores: four engines a hair apart, the whine
    const cores = [osc('sine', 1400), osc('sine', 1408), osc('triangle', 1393), osc('triangle', 1417)];
    const coreBand = filter('bandpass', 1400, 3), coreMix = gain(0.5), coreGain = gain(0);
    for (const o of cores) o.connect(coreMix);
    coreMix.connect(coreBand); coreBand.connect(coreGain); coreGain.connect(air);
    // the afterburner: the rumble and the ripping band over it, rippled together; the crackle
    const rumble = noise(), rumbleLow = filter('lowpass', 160, 0.7), rumbleLow2 = filter('lowpass', 160, 0.7), ripBand = filter('bandpass', 320, 0.9);
    const ripple = gain(0.4), burnerGain = gain(0), ripLevel = gain(0.45), rumbleLevel = gain(1.6);
    rumble.connect(rumbleLow); rumbleLow.connect(rumbleLow2); rumbleLow2.connect(rumbleLevel); rumbleLevel.connect(ripple);
    rumble.connect(ripBand); ripBand.connect(ripLevel); ripLevel.connect(ripple);
    ripple.connect(burnerGain); burnerGain.connect(air);
    const rippleA = osc('sine', 4.5), rippleB = osc('sine', 7.3), depthA = gain(0.22), depthB = gain(0.14);
    rippleA.connect(depthA); depthA.connect(ripple.gain); rippleB.connect(depthB); depthB.connect(ripple.gain);
    const wobbleSrc = noise(), wobble = filter('lowpass', 7, 0.7), wobbleDepth = gain(10);
    wobbleSrc.connect(wobble); wobble.connect(wobbleDepth); wobbleDepth.connect(ripple.gain);
    const crack = noise(), crackHi = filter('highpass', 1600, 0.7), crackVca = gain(0), crackleGain = gain(0);
    const ctl = noise(), ctlLow = filter('lowpass', 40, 0.7), ctlLevel = gain(11), shaper = ctx.createWaveShaper();
    shaper.curve = gateCurve(ctx);
    ctl.connect(ctlLow); ctlLow.connect(ctlLevel); ctlLevel.connect(shaper); shaper.connect(crackVca.gain);
    crack.connect(crackHi); crackHi.connect(crackVca); crackVca.connect(crackleGain); crackleGain.connect(air);
    // the wash: a low churn
    const wash = noise(), washBand = filter('lowpass', 260, 0.9);
    const washGain = gain(0), churnDepth = gain(0.35), washLevel = gain(0.65), churn = osc('sine', 1.7);
    wash.connect(washBand); washBand.connect(washLevel); washLevel.connect(washGain); churn.connect(churnDepth); churnDepth.connect(washLevel.gain); washGain.connect(air);
    for (const s of sources) s.start();
    return { out, air, side, fanBand, fanGain, hissGain, blade, bladeLow, bladeGain, cores, coreBand, coreGain, rumbleLow, rumbleLow2, ripBand, burnerGain, rippleA, rippleB, wobble, crackleGain, washGain, lit: 0, sources };
  }
}
